'use client'

import React from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import type { Article, Media, Team, VisaType } from '@/payload-types'
import { getMediaUrl } from '../../lib/media'

interface ArticleWithPreview extends Article {
  preview: string
}

interface ResourcesArticlesProps {
  articles: ArticleWithPreview[]
}

// Helper function to determine tag color based on content
function getTagVariant(label: string): string {
  const lowerLabel = label.toLowerCase()
  if (lowerLabel.includes('h-1b') || lowerLabel.includes('h1b')) return 'orange'
  if (lowerLabel.includes('green card') || lowerLabel.includes('low')) return 'green'
  if (lowerLabel.includes('policy') || lowerLabel.includes('update')) return 'blue'
  if (lowerLabel.includes('high') || lowerLabel.includes('urgent')) return 'red'
  return 'default'
}

// Determine card variant based on position in grid (3-column layout)
function getCardVariant(index: number): 'hero' | 'large' | 'standard' {
  if (index === 0) return 'hero'
  if (index === 3 || index === 6) return 'large'
  return 'standard'
}

export function ResourcesArticles({ articles }: ResourcesArticlesProps) {
  if (!articles || articles.length === 0) {
    return null
  }

  // Limit to first 9 articles for the resources page
  const displayedArticles = articles.slice(0, 9)

  return (
    <section className="py-8 md:py-12 pb-16 md:pb-20 px-6 md:px-12 lg:px-16">
      <div className="max-w-380 mx-auto">
        <div className="flex items-center justify-between mb-6 md:mb-8">
          <h2 className="text-2xl md:text-3xl font-medium text-gray-900">News &amp; Articles</h2>
          <Link
            href="/articles"
            className="inline-flex items-center gap-2 text-brand-blue hover:text-brand-blue/80 font-medium transition-colors"
          >
            View All
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-[280px]">
          {displayedArticles.map((article, index) => (
            <ArticleCard key={article.id} article={article} variant={getCardVariant(index)} />
          ))}
        </div>
      </div>
    </section>
  )
}

// Unified Article Card Component
function ArticleCard({
  article,
  variant,
}: {
  article: ArticleWithPreview
  variant: 'hero' | 'large' | 'standard'
}) {
  const featuredImage = article.featuredImage as Media | null | undefined
  const imageUrl = getMediaUrl(featuredImage, 'card')
  const visaTypes = article.visaTypes as VisaType[] | null | undefined
  const author = article.author as Team | null | undefined

  // Grid span classes based on variant
  const gridClasses = {
    hero: 'md:col-span-2 lg:col-span-2 md:row-span-2',
    large: 'lg:col-span-2',
    standard: '',
  }

  // Hero variant - overlay style
  if (variant === 'hero') {
    return (
      <Link
        href={`/articles/${article.slug}`}
        className={`group relative bg-brand-blue rounded-2xl overflow-hidden ${gridClasses[variant]}`}
      >
        {/* Background Image */}
        {imageUrl && (
          <Image
            src={imageUrl}
            alt={article.title || ''}
            fill
            className="object-cover opacity-40 group-hover:opacity-50 group-hover:scale-105 transition-all duration-500"
          />
        )}

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-linear-to-t from-brand-blue via-brand-blue/80 to-transparent" />

        {/* Content */}
        <div className="relative h-full flex flex-col justify-end p-6 md:p-8">
          {/* Tags */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-xs px-3 py-1 rounded-full font-medium bg-brand-gold/20 text-brand-gold-light border border-brand-gold/30">
              Featured
            </span>
            {visaTypes && visaTypes.length > 0 && (
              <span className="text-xs px-3 py-1 rounded-full font-medium bg-white/10 text-white/90 border border-white/20">
                {visaTypes[0].name}
              </span>
            )}
          </div>

          {/* Title */}
          <h3 className="text-2xl md:text-3xl lg:text-4xl font-serif text-white mb-4 group-hover:text-brand-gold-light transition-colors line-clamp-3">
            {article.title}
          </h3>

          {/* Preview */}
          <p className="text-white/70 text-sm md:text-base mb-4 line-clamp-2">{article.preview}</p>

          {/* Meta */}
          <div className="flex items-center gap-3 text-sm text-white/60">
            {author?.name && <span>{author.name}</span>}
            {author?.name && article.publishedDate && <span>•</span>}
            {article.publishedDate && (
              <span>
                {new Date(article.publishedDate).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
            )}
          </div>
        </div>
      </Link>
    )
  }

  // Large and Standard variants - card style with image on top
  return (
    <Link
      href={`/articles/${article.slug}`}
      className={`group bg-white rounded-2xl overflow-hidden border border-gray-200 hover:shadow-xl transition-all duration-300 flex flex-col ${gridClasses[variant]}`}
    >
      {/* Image - takes proportional space */}
      <div className="relative flex-1 min-h-0 overflow-hidden">
        {imageUrl ? (
          <Image
            src={imageUrl}
            alt={article.title || ''}
            fill
            className="object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="absolute inset-0 bg-linear-to-br from-gray-100 to-gray-200" />
        )}
      </div>

      {/* Content - fixed height area */}
      <div className="p-4 flex flex-col">
        {/* Tag */}
        {visaTypes && visaTypes.length > 0 && (
          <span
            className={`text-xs font-medium mb-2 ${
              getTagVariant(visaTypes[0].name || '') === 'orange'
                ? 'text-orange-600'
                : getTagVariant(visaTypes[0].name || '') === 'green'
                  ? 'text-green-600'
                  : getTagVariant(visaTypes[0].name || '') === 'blue'
                    ? 'text-blue-600'
                    : 'text-gray-600'
            }`}
          >
            {visaTypes[0].name}
          </span>
        )}

        {/* Title */}
        <h3 className="text-base font-medium text-gray-900 mb-2 group-hover:text-brand-blue transition-colors line-clamp-2">
          {article.title}
        </h3>

        {/* Preview - only show on large cards */}
        {variant === 'large' && (
          <p className="text-gray-600 text-sm mb-2 line-clamp-2">{article.preview}</p>
        )}

        {/* Meta */}
        <div className="flex items-center gap-2 text-xs text-gray-500 mt-auto">
          {author?.name && <span>{author.name}</span>}
          {author?.name && article.publishedDate && <span>•</span>}
          {article.publishedDate && (
            <span>
              {new Date(article.publishedDate).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
              })}
            </span>
          )}
        </div>
      </div>
    </Link>
  )
}
